import { useNavigate, useParams } from 'react-router-dom';
import { useCallback } from 'react';

import { useGetAvatarSeriesQuery, type AvatarItem } from '@/services/avatarApi';
import { AvatarSkeleton } from '@/components/avatars/AvatarSkeleton';

export function AvatarSeries() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: items = [], isLoading } = useGetAvatarSeriesQuery(id, { skip: !id });

  const onCardClick = useCallback(
    (avatar: AvatarItem) => navigate(`/avatars/${avatar._id}`, { replace: true }),
    [navigate],
  );

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">Series</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {isLoading
            ? 'Loading versions…'
            : `${items.length} version${items.length === 1 ? '' : 's'}, newest first.`}
        </p>
      </header>

      {isLoading ? (
        <AvatarSkeleton />
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          No versions yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {items.map((avatar) => (
            <SeriesCardWrapper key={avatar._id} avatar={avatar} onClick={onCardClick} />
          ))}
        </div>
      )}
    </div>
  );
}

function SeriesCardWrapper({
  avatar,
  onClick,
}: {
  avatar: AvatarItem;
  onClick: (avatar: AvatarItem) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(avatar)}
      className="flex flex-col overflow-hidden rounded-xl bg-bg-secondary text-left transition-opacity active:opacity-80"
    >
      <div className="aspect-square overflow-hidden border border-black/40 bg-black">
        {avatar.thumbnailUrl && (
          <img
            src={avatar.thumbnailUrl}
            alt={avatar.title}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        )}
      </div>
      <div className="p-2">
        <p className="truncate text-[13px] font-semibold text-fg">{avatar.title}</p>
      </div>
    </button>
  );
}
